/**
 * ImageBlock - Enhanced image component with layout options
 * Supports captions, positioning, and responsive behavior
 */

import React from 'react'
import { Box } from '@mui/material'
import Image from '../elements/Image'
import Paragraph from '../elements/Paragraph'

interface ImageBlockProps {
	src: string
	alt: string
	caption?: string
	width?: number | string
	height?: number | string
	fit?: 'cover' | 'contain' | 'fill'
	position?: 'left' | 'center' | 'right'
	rounded?: boolean
	shadow?: boolean
	maxWidth?: string
}

const ImageBlock: React.FC<ImageBlockProps> = ({
	src,
	alt,
	caption,
	width = 'auto',
	height = 'auto',
	fit = 'cover',
	position = 'center',
	rounded = false,
	shadow = false,
	maxWidth = '100%'
}) => {
	const getAlignment = () => {
		switch (position) {
			case 'left':
				return 'flex-start'
			case 'center':
				return 'center'
			case 'right':
				return 'flex-end'
			default:
				return 'center'
		}
	}

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: getAlignment(),
				maxWidth: maxWidth,
				mx: position === 'center' ? 'auto' : undefined
			}}
		>
			<Box
				sx={{
					boxShadow: shadow ? 2 : 0,
					borderRadius: rounded ? 1 : 0,
					overflow: rounded ? 'hidden' : 'visible'
				}}
			>
				<Image src={src} alt={alt} width={width} height={height} fit={fit} rounded={rounded} />
			</Box>

			{caption && (
				<Box sx={{ mt: 1, textAlign: position }}>
					<Paragraph size="small" color="secondary">
						{caption}
					</Paragraph>
				</Box>
			)}
		</Box>
	)
}

export default ImageBlock
